
import './App.css';
import React, { useRef, useState, useEffect } from 'react';
import { flushSync } from 'react-dom';
import ChatMessage from './components/chat/ChatMessage';
import ChatHistory from './components/chat/ChatHistory';
import ChatInput from './components/chat/ChatInput';
import ChatWelcome from './components/chat/ChatWelcome';
import SuggestedQuestions from './components/chat/SuggestedQuestions';
import { API_URL, isProd, FEATURES, REGION } from './config';
import Greeting from './components/layout/Greeting';

import TopBar from './components/layout/TopBar';
import IssueFeedback from './components/feedback/IssueFeedback';
import HistoryPanel from './components/chat/HistoryPanel';

// 历史会话详情每页条数。后端分页语义：page=1 返回最新一页（页内按时间升序），
// 页码越大越早；因此“加载更早”即请求下一页并插入到顶部之前。
const HISTORY_PAGE_SIZE = 10;

const ChatUI = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const userId = queryParams.get("userId") || (isProd ? "" : "55555");
  const productType = queryParams.get("productType") || (isProd ? "" : "5");
  const param = queryParams.get("param");
  const emailFromUrl = queryParams.get("email") || "";
  const phoneFromUrl = queryParams.get("phone") || "";

  const [conversationId, setConversationId] = useState("");
  const [messages, setMessages] = useState([]);
  const [messagesHistroy, setMessagesHistroy] = useState([]);
  const [loadingText, setLoadingText] = useState('Generating, please wait');
  const [isWaitingForAI, setWaitingForAI] = useState(false);
  const [submittedMsgIds, setSubmittedMsgIds] = useState(new Set());
  const [showHistory, setShowHistory] = useState(false);
  const [conversationGroups, setConversationGroups] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [loadingEarlier, setLoadingEarlier] = useState(false); // 是否正在加载更早一页（按钮转圈）
  const [historyTotal, setHistoryTotal] = useState(0);         // 当前会话消息总数（用于判断是否还有更早分页）
  const [current, setCurrent] = useState("Main");
  const savedScrollRef = useRef(0);
  const chatHistoryRef = useRef(null);
  const chatMainRef = useRef(null);
  const dotsIntervalRef = useRef(null);
  const sendMessageRef = useRef(null);
  const nextMessageIdRef = useRef(0);
  const loadedPageRef = useRef(0);              // 已加载到的最大页码（page=1 为最新一页）
  const pendingEarlierScrollRef = useRef(null); // 顶部插入更早消息后，用于回正阅读位置的滚动指标
  const isLoadingEarlierRef = useRef(false);    // 是否有“加载更早”请求在途（同步去重，防连点）

  const getNextMessageId = () => {
    nextMessageIdRef.current += 1;
    return nextMessageIdRef.current;
  };

  const handleSendMessage = (message, bUser, bError = false, answer, isStreaming, settingTool, noSubmitButton = false) => {
    if (isStreaming === 0) {
      const messageId = getNextMessageId();
      setMessages(prev => [...prev, { id: messageId, text: "", isUser: false, isError: false, isComplete: false }]);
    }
    else if (isStreaming === 1) {
      try {
        // C. 核心：更新数组中最后一个助手的消息内容
        //console.log("Streaming message chunk:", message);
        flushSync(() => {
          setWaitingForAI(false);
          setMessages(prev => {
            const newMessages = [...prev];
            const lastIdx = newMessages.length - 1;
            // 找到刚才那个占位对象并追加文本
            newMessages[lastIdx] = {
              ...newMessages[lastIdx],
              text: newMessages[lastIdx].text + message,
              tool: settingTool
            };
            return newMessages;
          });
        });

      } catch (e) {
        console.error("解析流式JSON失败", e);
      }
    } else if (isStreaming === 2) {//调参
      flushSync(() => {
        setWaitingForAI(false);
        setMessages(prev => {
          const newMessages = [...prev];
          const lastIdx = newMessages.length - 1;
          // 找到刚才那个占位对象并追加文本
          newMessages[lastIdx] = {
            ...newMessages[lastIdx],
            text: message,
            tool: settingTool,
            isComplete: true
          };
          return newMessages;
        });
      });
    }
    else if (isStreaming === 3) {
      // 信号：渲染完成
      flushSync(() => {
        setWaitingForAI(false);
        setMessages(prev => {
          const newMessages = [...prev];
          if (newMessages.length > 0) {
            newMessages[newMessages.length - 1].isComplete = true;
          }
          return newMessages;
        });
      });
    }
    else {
      const messageId = getNextMessageId();
      setMessages(prevMessages => [...prevMessages, { id: messageId, text: message, isUser: bUser, isError: bError, answer: answer, tool: settingTool, isComplete: true, noSubmitButton }]);
      if (bUser) setWaitingForAI(true);
    }
  };


  const onButtonClick = (value) => {
    console.log("onButtonClick: " + value)
    if (sendMessageRef.current) {
      sendMessageRef.current.ParentHandleSendMessage(value, productType);
    }
  }

  const getDetailList = async (conversationIdTemp, page = 1) => {
    let httpRequest = new XMLHttpRequest();
    httpRequest.open('GET', `${API_URL}/users/${userId}/conversations/${conversationIdTemp}/messages?page=${page}&count=${HISTORY_PAGE_SIZE}`, true);
    httpRequest.send();

    httpRequest.onreadystatechange = function () {
      if (httpRequest.readyState !== 4) return; // 请求未完成（中间状态）时不处理，避免误报
      if (httpRequest.status === 200) {
        let json = httpRequest.responseText;
        callbackGetListDetail(json, page);
      } else {
        setDetailLoading(false);
        setLoadingEarlier(false);
        isLoadingEarlierRef.current = false;
        console.error(`Request failed with status: ${httpRequest.status}, response: ${httpRequest.responseText}`);
      }
    };
  }

  // page=1：最新一页，整体替换并滚到底部；page>1：更早一页，插入到顶部之前并保持阅读位置。
  function callbackGetListDetail(json, page = 1) {
    try {
      let jsonObject = window.JSON.parse(json);
      console.log(jsonObject)
      if (jsonObject.code === 200 && jsonObject.data) {
        const rawList = Array.isArray(jsonObject.data.list) ? jsonObject.data.list : [];

        // --- 开始重新映射（id 用 page-index 保证跨页唯一，避免插入时 React key 冲突）---
        const mappedList = rawList.map((item, index) => {
          return {
            question: item.humanMessage,
            answer: item.aiMessage,
            createdAt: new Date().toLocaleTimeString(), // 添加新字段
            earSide: item.earSide,
            tool: { strategy: item.soundAdjustmentStrategy, leftOrRight: item.earSide, value: item.value },
            id: `${page}-${index}`
          };
        });

        if (typeof jsonObject.data.total === 'number') {
          setHistoryTotal(jsonObject.data.total);
        }
        loadedPageRef.current = page;

        if (page <= 1) {
          // 最新一页：直接展示（空会话则回落到“常见问题”）
          setMessagesHistroy(mappedList);
        } else {
          // 更早一页：先记录当前滚动指标（此刻 DOM 仍是插入前状态），再插入到顶部之前；
          // 插入后在滚动 effect 里按高度差回正，保证用户当前阅读位置不跳动
          const el = document.scrollingElement || document.documentElement;
          pendingEarlierScrollRef.current = { prevHeight: el.scrollHeight, prevTop: el.scrollTop };
          setMessagesHistroy(prev => [...mappedList, ...prev]);
        }
      }
    } catch (error) {
      // 如果上面的操作抛出异常，这里的代码将会执行
      console.error('Error during data fetching:', error);
    } finally {
      // 数据落地后再撤掉 loading：先 setMessagesHistroy 再关 loading，
      // 避免出现 loading 已关、消息还没渲染从而闪一下“常见问题”初始态
      setDetailLoading(false);
      setLoadingEarlier(false);
      isLoadingEarlierRef.current = false;
    }
  };

  // 点击“加载更早”：请求下一页（更早）并插入到顶部之前。isLoadingEarlierRef 同步去重防连点。
  const loadEarlierMessages = () => {
    if (isLoadingEarlierRef.current) return;
    if (!conversationId) return;
    if (messagesHistroy.length >= historyTotal) return; // 已全部加载
    isLoadingEarlierRef.current = true;
    setLoadingEarlier(true);
    getDetailList(conversationId, loadedPageRef.current + 1);
  };

  function callbackGetList(json) {
    try {
      let jsonObject = window.JSON.parse(json);
      console.log(jsonObject)
      if (jsonObject.code === 200) {
        // 历史页按需展示完整分组列表（today/thisWeek/earlier）
        setConversationGroups(jsonObject.data || {});
      }
    } catch (error) {
      // 如果上面的操作抛出异常，这里的代码将会执行
      console.error('Error during data fetching:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const getHistoryList = async (callback, fingerprintTmp, productType) => {
    let httpRequest = new XMLHttpRequest();
    console.log("Fetching history list with fingerprint:", fingerprintTmp, "and productType:", productType);
    httpRequest.open('GET', `${API_URL}/users/${fingerprintTmp}/${productType}/conversations`, true);
    httpRequest.send();

    httpRequest.onreadystatechange = function () {//
      if (httpRequest.readyState !== 4) return; // 请求未完成（中间状态）时不处理，避免误报
      if (httpRequest.status === 200) {
        let json = httpRequest.responseText;
        callback(json);
      } else {
        setHistoryLoading(false);
        console.error(`Request failed with status: ${httpRequest.status}, response: ${httpRequest.responseText}`);
      }
    };
  }


  // 在组件挂载后和 messages 更新后滚动到底部
  useEffect(() => {
    dotsIntervalRef.current = setInterval(() => {
      setLoadingText((prevText) => {
        // 根据当前文本决定下一个文本
        switch (prevText) {
          case 'Let me think for a moment':
            return 'Let me think for a moment.';
          case 'Let me think for a moment.':
            return 'Let me think for a moment..';
          case 'Let me think for a moment..':
            return 'Let me think for a moment...';
          case 'Let me think for a moment...':
          default:
            return 'Let me think for a moment';
        }
      });
    }, 500);

    // 清除定时器，防止在组件卸载时内存泄漏
    return () => clearInterval(dotsIntervalRef.current);
  }, [messages]);

  const hasCalled = useRef(false); // 标记是否已调用
  // 处理一次性调用的 useEffect
  useEffect(() => {
    // 一次性调用逻辑
    console.log('This runs only once when the component mounts');
    if (!hasCalled.current) {
      hasCalled.current = true;
      if (param !== '' && param !== null && sendMessageRef.current) {
        // 外部传入问题：等同新建会话，进入即空白新会话，不在挂载时加载历史
        sendMessageRef.current.ParentHandleSendMessage(param, productType);
      }
      // 历史记录改为在打开历史页时按需加载（openHistory），挂载时不再内联自动加载，
      // 从而避免：①外部问题被旧历史污染 ②历史与实时流竞态导致重复 ③常见问题列表闪现被覆盖
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 空数组表示只在组件挂载时运行一次

  useEffect(() => {
    // 加载更早分页：在顶部插入了更早消息，按高度差回正滚动位置（保持当前阅读位置），不滚到底部
    if (pendingEarlierScrollRef.current) {
      const pending = pendingEarlierScrollRef.current;
      pendingEarlierScrollRef.current = null;
      setTimeout(() => {
        const el = document.scrollingElement || document.documentElement;
        const delta = el.scrollHeight - pending.prevHeight;
        window.scrollTo({ top: pending.prevTop + delta, behavior: 'instant' });
      }, 0);
      return;
    }
    // 其余情况（新消息 / 打开会话）：滚动到聊天窗口底部
    setTimeout(() => {
      if (chatHistoryRef.current) {
        window.scrollTo({
          top: chatHistoryRef.current.offsetTop + chatHistoryRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 0);
  }, [messages, messagesHistroy]); // 依赖数组messages和messagesHistroy

  const handleNewChat = () => {
    setCurrent("Main");
    setConversationId("");
    if (sendMessageRef.current) {
      sendMessageRef.current.childFunction();
    }
    console.log("Starting new chat, conversationId reset to empty.", conversationId);
    setMessages([]);
    setMessagesHistroy([]);
    setHistoryTotal(0);
    loadedPageRef.current = 0;
    isLoadingEarlierRef.current = false;
    pendingEarlierScrollRef.current = null;
    setLoadingEarlier(false);
    setShowHistory(false);
  }

  // 打开历史会话页：按需拉取会话列表
  const openHistory = () => {
    setShowHistory(true);
    setHistoryLoading(true);
    getHistoryList(callbackGetList, userId, productType);
  };

  // 在历史页选择某条会话：加载该会话消息到对话页，并允许在该会话基础上继续追问
  const handleSelectConversation = (conversationIdTemp) => {
    setCurrent("Main");
    setMessages([]);
    setMessagesHistroy([]);
    setHistoryTotal(0);
    loadedPageRef.current = 0;
    isLoadingEarlierRef.current = false;
    pendingEarlierScrollRef.current = null;
    setLoadingEarlier(false);
    setDetailLoading(true);
    setConversationId(conversationIdTemp);
    if (sendMessageRef.current) {
      // 重置 ChatInput 内部缓存的会话 ID，后续以 userConversationId 这条为准继续对话
      sendMessageRef.current.childFunction();
    }
    getDetailList(conversationIdTemp, 1);
    setShowHistory(false);
  };


  const handleSubmit = (msgId) => {
    savedScrollRef.current = window.scrollY;
    setCurrent("IssueFeedback");
    window.scrollTo({ top: 0, behavior: 'instant' });
    if (msgId !== undefined) {
      setTimeout(() => {
        setSubmittedMsgIds(prev => new Set([...prev, msgId]));
      }, 500);
    }
  }

  const handleBackPro = () => {
    setCurrent("Main");
    setTimeout(() => {
      window.scrollTo({ top: savedScrollRef.current, behavior: 'instant' });
    }, 0);
  }

  const handleSubmitComplete = () => {
    setCurrent("Main");
    setTimeout(() => {
      handleSendMessage(
        "✅ Your request has been submitted. Our support team will review your request and contact you soon.",
        false, false, null, undefined, null, true
      );
    }, 100);
  };

  return (
    <div className="container" ref={chatMainRef}>
      {FEATURES.showWebTopBar && (
        <div className="top-container" >
          <TopBar
            handleNewChat={handleNewChat}
            handleBack={handleBackPro}
            bShowBackButton={current === "IssueFeedback"}
            onOpenHistory={openHistory}
            showHistoryEntry={current === "Main"}
            showClose={FEATURES.showChatClose}
          ></TopBar>
        </div>
      )}
      <div style={{ display: current === "Main" ? '' : 'none' }}>
      <Greeting></Greeting>
        <div>
          <div className="chat-container" ref={chatHistoryRef}>
            <div className="chat-history-history">
              {/* {
            messagesHistroy.length === 0 &&  messages.length === 0 &&
            (<div>
              <ChatWelcome />
              
            </div>)
          } */}

              {detailLoading && messagesHistroy.length === 0 &&
                <div className="chat-loading">
                  <span className="chat-loading-spinner"></span>
                </div>
              }

              {!detailLoading && messagesHistroy.length === 0 && messages.length === 0 &&
                <div>
                  <SuggestedQuestions onClick={onButtonClick} productType={productType}></SuggestedQuestions>
                </div>
              }

              {/* 顶部“加载更早”：已加载条数仍少于总数时显示，点击向上分页加载更早的消息 */}
              {!detailLoading && messagesHistroy.length > 0 && messagesHistroy.length < historyTotal &&
                <div className="load-earlier">
                  <button className="load-earlier-btn" onClick={loadEarlierMessages} disabled={loadingEarlier}>
                    {loadingEarlier
                      ? <span className="chat-loading-spinner load-earlier-spinner"></span>
                      : 'Load earlier messages'}
                  </button>
                </div>
              }

              {messagesHistroy.reduce((acc, msg, index) => {
                const prevMsg = acc.length > 0 ? acc[acc.length - 1].props.message.createdAt : null;

                acc.push(
                  <ChatHistory
                    key={msg.id}
                    message={msg}
                    prevUpdatedAt={prevMsg}
                    tool={msg.tool}
                    onSubmit={handleSubmit}
                  />
                );
                return acc;
              }, [])}
            </div>

            <div className="chat-history" >
              {messages.map((msg) => {
                const isLastTool = [...messages].reverse().find(m => m.tool)?.id === msg.id;
                return (
                  <ChatMessage key={msg.id} msgId={msg.id} message={msg.text} isUser={msg.isUser}
                    isError={msg.isError} answers={msg.answer} tool={msg.tool} isLast={isLastTool} onSubmit={handleSubmit}
                    isComplete={msg.isComplete} isSubmitted={submittedMsgIds.has(msg.id)} noSubmitButton={msg.noSubmitButton} />);

              })}
            </div>

            {isWaitingForAI && (
              <div className='container-left'>
                <label className='AIHeadLabel'>
                </label>

                <div className='chat-message-bot'>
                  {loadingText}
                </div>
              </div>
            )}

            {
              messagesHistroy.length + messages.length <= 2 &&
              (<div>
                <div className="footer-placeholder"></div>
                <div className="footer-placeholder"></div>
              </div>)
            }
          </div>
          <div className="footer-placeholder"></div>
          <div className="input-container">
            <ChatInput ref={sendMessageRef}
              onSendMessage={handleSendMessage} userConversationId={conversationId} onSubmit={handleSubmit}
              userId={userId} productType={productType} />
          </div>
        </div>
      </div>
      {FEATURES.showFeedback && current === "IssueFeedback" && <IssueFeedback onRequestComplete={handleSubmitComplete} productType={productType} defaultEmail={emailFromUrl} defaultPhone={phoneFromUrl} />}

      <HistoryPanel
        show={showHistory}
        groups={conversationGroups}
        loading={historyLoading}
        hasBack={FEATURES.historyHasBack}
        closeExitsApp={FEATURES.historyCloseExitsApp}
        onBack={() => setShowHistory(false)}
        onClose={() => setShowHistory(false)}
        onSelect={handleSelectConversation}
      />

    </div>

  );
};



function App() {
  return (
    <div className="App">
      <header className={`App-header${REGION === 'uk' ? ' uk' : ''}`}>
        <ChatUI></ChatUI>
      </header>
    </div>
  );
}

export default App;
